import React, { useState, useCallback, useMemo } from 'react';
import { devLog } from '../utils/devLog';

interface Document {
  id: string;
  name: string;
  type?: string;
  size?: number;
  status?: string;
  pages?: number;
  extractedText?: string;
}

interface DocumentCombinationProps {
  documents: Document[];
  onCombinationReady: (combinedDocument: Document | null, sourceDocumentIds?: string[]) => void;
  onDocumentsUpdated?: (documents: Document[]) => void;
}

export const DocumentCombination: React.FC<DocumentCombinationProps> = ({
  documents,
  onCombinationReady,
  onDocumentsUpdated
}) => {
  const [showCombineInterface, setShowCombineInterface] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [combinationName, setCombinationName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [combinedDocument, setCombinedDocument] = useState<Document | null>(null);
  const [orderedDocuments, setOrderedDocuments] = useState<Document[]>([]);

  // Initialize ordered documents when selection changes
  React.useEffect(() => {
    const selected = documents.filter(doc => selectedDocuments.includes(doc.id));
    setOrderedDocuments(selected);
  }, [selectedDocuments, documents]);

  // Auto-generate combination name
  React.useEffect(() => {
    if (selectedDocuments.length > 0) {
      const selectedDocs = documents.filter(doc => selectedDocuments.includes(doc.id));
      if (selectedDocs.length === 2) {
        setCombinationName(`${selectedDocs[0].name} + ${selectedDocs[1].name}`);
      } else if (selectedDocs.length > 2) {
        setCombinationName(`${selectedDocs[0].name} + ${selectedDocs.length - 1} more`);
      } else {
        setCombinationName(selectedDocs[0]?.name || '');
      }
    }
  }, [selectedDocuments, documents]);

  const handleDocumentSelection = useCallback((documentId: string, selected: boolean) => {
    setSelectedDocuments(prev => 
      selected 
        ? [...prev, documentId]
        : prev.filter(id => id !== documentId)
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  }, [documents, selectedDocuments.length]);

  const moveDocumentUp = useCallback((index: number) => {
    if (index === 0) return;
    const items = Array.from(orderedDocuments);
    const [item] = items.splice(index, 1);
    items.splice(index - 1, 0, item);
    setOrderedDocuments(items);
  }, [orderedDocuments]);

  const moveDocumentDown = useCallback((index: number) => {
    if (index === orderedDocuments.length - 1) return;
    const items = Array.from(orderedDocuments);
    const [item] = items.splice(index, 1);
    items.splice(index + 1, 0, item);
    setOrderedDocuments(items);
  }, [orderedDocuments]);

  const handleCombineDocuments = async () => {
    if (selectedDocuments.length < 2) return;

    setIsProcessing(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.avery.cloud';
      const token = localStorage.getItem('riskninja-token');

      // Debug: Log what we're sending
      const documentIds = orderedDocuments.map(doc => doc.id);
      devLog('🔄 Combining documents:', {
        documentIds,
        documentNames: orderedDocuments.map(doc => doc.name),
        combinationName,
        apiUrl: `${API_BASE_URL}/api/document-combination/combine`
      });

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      if (documentIds.length < 2) {
        throw new Error('At least 2 documents are required for combination');
      }

      const response = await fetch(`${API_BASE_URL}/api/document-combination/combine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          documentIds,
          name: combinationName,
          preserveMetadata: true,
          addSeparators: true
        })
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error(`Server returned invalid response (status ${response.status})`);
      }

      if (response.ok && responseData.success) {
        const combined = responseData.data.combinedDocument;
        const combinedDoc: Document = {
          id: combined.id,
          name: combined.name,
          type: 'combined',
          size: combined.size,
          status: 'ready',
          pages: combined.pageCount,
          extractedText: combined.extractedText
        };

        setCombinedDocument(combinedDoc);
        onCombinationReady(combinedDoc, selectedDocuments);

        // Update documents list to include combined document
        const remainingDocs = documents.filter(doc => !selectedDocuments.includes(doc.id));
        const updatedDocs = [...remainingDocs, combinedDoc];
        onDocumentsUpdated?.(updatedDocs);

        // Reset interface
        setShowCombineInterface(false);
        setSelectedDocuments([]);
      } else {
        // Get the actual error message from the backend
        const errorMessage = responseData.error || 'Failed to combine documents';
        console.error('Document combination failed:', { status: response.status, error: errorMessage, response: responseData });
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error combining documents:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to combine documents: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveCombination = () => {
    setCombinedDocument(null);
    onCombinationReady(null);
    
    // Restore original documents
    onDocumentsUpdated?.(documents.filter(doc => doc.type !== 'combined'));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentIcon = (doc: Document) => {
    if (doc.type === 'combined') {
      return (
        <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
      </svg>
    );
  };

  const canCombine = useMemo(() => {
    return selectedDocuments.length >= 2 && combinationName.trim().length > 0;
  }, [selectedDocuments.length, combinationName]);

  // Filter documents that are ready for combination (more permissive)
  const processedDocuments = documents.filter(doc => {
    // Include document if it has completed upload or doesn't have explicit error status
    return !doc.status || doc.status !== 'uploading' && doc.status !== 'error';
  });

  // Debug logging
  console.log('DocumentCombination Debug:', {
    totalDocuments: documents.length,
    processedDocuments: processedDocuments.length,
    documentStatuses: documents.map(d => ({ name: d.name, status: d.status })),
    documentsData: documents
  });

  // Show combination interface if we have at least 2 documents
  if (documents.length < 2) {
    return null; // Don't show if less than 2 documents total
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Combined Document Display */}
      {combinedDocument && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getDocumentIcon(combinedDocument)}
              <div>
                <h4 className="font-medium text-purple-900 dark:text-purple-100">
                  Combined Document: {combinedDocument.name}
                </h4>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  {combinedDocument.pages} pages • {formatFileSize(combinedDocument.size || 0)}
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveCombination}
              className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
              title="Remove combination"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Combination Interface Toggle */}
      {!combinedDocument && (
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              Document Combination
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Combine multiple documents into a single document for analysis
            </p>
          </div>
          <button
            onClick={() => setShowCombineInterface(!showCombineInterface)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              showCombineInterface
                ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
            }`}
          >
            {showCombineInterface ? 'Cancel' : 'Combine Documents'}
          </button>
        </div>
      )}

      {/* Combination Interface */}
      {showCombineInterface && !combinedDocument && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="font-medium text-gray-900 dark:text-white">
              Select Documents to Combine ({selectedDocuments.length} selected)
            </h5>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
            >
              {selectedDocuments.length === documents.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Document Selection */}
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                <input
                  type="checkbox"
                  id={`doc-${doc.id}`}
                  checked={selectedDocuments.includes(doc.id)}
                  onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1 flex items-center space-x-3">
                  {getDocumentIcon(doc)}
                  <div className="flex-1">
                    <label htmlFor={`doc-${doc.id}`} className="font-medium text-gray-900 dark:text-white cursor-pointer">
                      {doc.name}
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {doc.type} • {doc.pages} pages • {formatFileSize(doc.size || 0)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Document Order Configuration */}
          {selectedDocuments.length > 1 && (
            <div className="space-y-3">
              <h6 className="font-medium text-gray-900 dark:text-white">
                Combination Order
              </h6>
              <div className="space-y-2">
                {orderedDocuments.map((doc, index) => (
                  <div
                    key={doc.id}
                    className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded"
                  >
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{doc.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{doc.type}</p>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => moveDocumentUp(index)}
                        disabled={index === 0}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveDocumentDown(index)}
                        disabled={index === orderedDocuments.length - 1}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Combination Name */}
          {selectedDocuments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Combined Document Name
              </label>
              <input
                type="text"
                value={combinationName}
                onChange={(e) => setCombinationName(e.target.value)}
                placeholder="Enter name for combined document"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} selected
              {selectedDocuments.length >= 2 && ' • Ready to combine'}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCombineInterface(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCombineDocuments}
                disabled={!canCombine || isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Combining...</span>
                  </>
                ) : (
                  <span>Combine Documents</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentCombination;